import { useEffect, useMemo, useState, useCallback } from "react";
import { Button, Form } from "antd";
import { Body } from "../../../components/main-body/body";
import editIcon from "../../../assets/edit.svg";
import { useTranslation } from "react-i18next";
import useNotification from "../../../components/notification/notification";
import "./life-points.css";
import ApiLifePointsManagement from "../../../api/lifepoints";
import { FormInput } from "../../../components/form/form";
import classNames from "classnames";
import { TableCell } from "../../../components/table/table-cell/table-cell";
import InfinityTable from "../../../components/infinity-table/infinity-table";
import DropdownFilters from "../../../components/dropdown-filters/dropdown-filters";
import debounce from "../../../common/utils/debounce";
import useAdminEvents from "../../../hooks/useAdminEvents";

const SORT_FIELDS = {
  name: "name",
  type: "type",
  points: "points",
};

const LifePoints = () => {
  const { t } = useTranslation();
  const [isEdit, setIsEdit] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const { openNotification } = useNotification();
  const [pointsList, setPointsList] = useState([]);
  const [initialList, setInitial] = useState([]);
  const [form] = Form.useForm();
  const [filters, setFilters] = useState({});
  const [search, setSearch] = useState("");

  const [sort, setSort] = useState({ field: null, order: null });

  const apiParams = useMemo(() => {
    const rawType = filters?.type;
    const type =
      Array.isArray(rawType) ? rawType[0] : rawType || undefined;

    const sortBy = sort?.field || 'name';
    const sortOrder = sort?.order || 'ASC';

    return {
      ...(type ? { type } : {}),
      ...(search && search.trim() ? { search: search.trim() } : {}),
      sortBy,
      sortOrder,
    };
  }, [filters, sort, search]);

  const cancel = () => {
    setIsEdit(false);
    setHasChanges(false);
    setPointsList(initialList);
  };

  const toogleEdit = () => {
    setIsEdit(!isEdit);
    setHasChanges(false);
  };

  const updatePoints = async () => {
    await ApiLifePointsManagement.update(pointsList);
    openNotification({ type: "success", message: <p>{t("lifepoints.notification.success")}</p> });
    setIsEdit(false);
    setHasChanges(false);
    setInitial(pointsList);
  };

  // const getLifePoints = async (srt = sort) => {
  //   const r = await ApiLifePointsManagement.get({
  //     ...filters,
  //     sortBy: srt.field,
  //     sortOrder: srt.order,
  //   });
  //   setPointsList(r.data.scores || []);
  //   setInitial(r.data.scores || []);
  // };
  const getLifePoints = useCallback(async (params) => {
    const r = await ApiLifePointsManagement.get(params);
    const list = r?.data?.scores || [];
    setPointsList(list);
    setInitial(list);
  }, []);

  const refreshLifePoints = useCallback(async () => {
    setIsEdit(false);
    setHasChanges(false);
    await getLifePoints(apiParams);
  }, [apiParams, getLifePoints]);

  useAdminEvents({
    onLifePointChanged: refreshLifePoints,
    onCategoryChanged: refreshLifePoints,
  });

  const debouncedGetLifePoints = useMemo(
    () => debounce(getLifePoints, 400),
    [getLifePoints]
  );

  const onHandleChange = (name, v) => {
    if (!hasChanges) setHasChanges(true);
    setPointsList(prev =>
      prev.map(item => (item.name === name ? { ...item, points: +v.target.value } : item))
    );
  };

  useEffect(() => { getLifePoints(); /* eslint-disable-line */ }, []);

  useEffect(() => { getLifePoints(sort); /* eslint-disable-line */ }, [sort.field, sort.order]);

  const handleTableChange = (_pagination, _filters, sorter) => {
    const fieldKey = sorter?.columnKey || sorter?.field;
    if (!sorter?.order) {
      setSort({ field: SORT_FIELDS.name, order: "ASC" });
      return;
    }
    setSort({
      field: fieldKey,
      order: sorter.order === "ascend" ? "ASC" : "DESC",
    });
  };

  useEffect(() => {
    getLifePoints(apiParams);
  }, [sort]);

  useEffect(() => {
    debouncedGetLifePoints(apiParams);
  }, [filters, search]);

  useEffect(() => {
    getLifePoints(apiParams);
  }, []);

  const tableColumns = useMemo(
    () => [
      {
        title: t("lifepoints.table.activity"),
        dataIndex: "name",
        key: SORT_FIELDS.name,
        width: "50%",
        sorter: true,
        sortOrder:
          sort.field === SORT_FIELDS.name
            ? sort.order === "ASC"
              ? "ascend"
              : "descend"
            : null,
        render: (_, item) => <TableCell text={item.name} />,
      },
      {
        title: "Type",
        dataIndex: "type",
        key: SORT_FIELDS.type,
        width: "20%",
        sorter: true,
        sortOrder:
          sort.field === SORT_FIELDS.type
            ? sort.order === "ASC"
              ? "ascend"
              : "descend"
            : null,
        render: (_, item) => (
          <TableCell text={item.type === "category" ? "Category" : "Activity"} />
        ),
      },
      {
        title: t("lifepoints.table.points"),
        dataIndex: "points",
        key: SORT_FIELDS.points,
        width: "30%",
        sorter: true,
        sortOrder:
          sort.field === SORT_FIELDS.points
            ? sort.order === "ASC"
              ? "ascend"
              : "descend"
            : null,
        render: (_, item) => (
          <FormInput
            name={item.name}
            key={item.id}
            readOnly={!isEdit}
            type="number"
            className={classNames("lifepoints-content-item-points", {
              "lifepoints-content-item-points--edit-mode": isEdit,
            })}
            value={item.points}
            bordered={false}
            onChange={(v) => onHandleChange(item.name, v)}
          />
        ),
      },
    ],
    [isEdit, t, sort]
  );

  const typesList = [{ id: 'category', name: 'Category'}, { id: 'activity', name: 'Activity'}]

  const updateFilterValue = (value) => setFilters(value);

  return (
    <Body
      header={{
        title: t("lifepoints.title"),
        search: {
          value: search,
          onHandleChange: setSearch,
        }
      }}
      filters={
        <DropdownFilters
          filtersBy={[
            { name: "Type", id: "type", children: typesList },
          ]}
          updateFilterValue={updateFilterValue}
        />
      }
    >
      <div className="lifepoints-container profile_form-container">
        <div className="profile_form__header">
          <span className="profile_form__header__title">
            {t("lifepoints.mapping")}
          </span>
          <div style={{ height: 40 }}>
            {isEdit ? (
              <div className="profile-header__btns">
                <Button
                  style={{ height: 40, background: "rgba(108, 108, 137, 0.08)" }}
                  onClick={cancel}
                >
                  {t("profile.btns.cancel")}
                </Button>
                <Button
                  type="primary"
                  style={{ marginLeft: 8, height: 40 }}
                  onClick={updatePoints}
                  disabled={!hasChanges}
                >
                  {t("profile.btns.save")}
                </Button>
              </div>
            ) : (
              <img
                className="profile_form__header__edit-btn"
                src={editIcon}
                alt="edit"
                role="presentation"
                onClick={toogleEdit}
              />
            )}
          </div>
        </div>
      </div>

      <div className="lifepoints-table">
        <Form form={form} component={false} preserve>
          <InfinityTable
            data={pointsList}
            tableColumns={tableColumns}
            rowKey="id"
            isLoading={false}
            onChange={handleTableChange}
          />
        </Form>
      </div>
    </Body>
  );
};

export default LifePoints;
